import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";
import { VAT_RATE, vatOf, toIncl } from "@/lib/totals";

export { VAT_RATE };

const STORAGE_KEY = "elvion_vat_pref_v1"; // 'incl' | 'excl'

type VatCtx = {
  /** True = prijzen incl. btw, false = ex. btw */
  inclVat: boolean;
  /** Een gebruiker heeft expliciet voorkeur ingesteld */
  userOverride: boolean;
  setInclVat: (incl: boolean) => void;
  toggle: () => void;
};

const Ctx = createContext<VatCtx | null>(null);

export function VatProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const [override, setOverride] = useState<"incl" | "excl" | null>(null);

  // Load saved preference once
  useEffect(() => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
      if (raw === "incl" || raw === "excl") setOverride(raw);
    } catch {}
  }, []);

  // Alle prijzen worden altijd incl. btw getoond — geen ex-btw weergave meer,
  // om verwarring bij klanten te voorkomen.
  const inclVat = true;

  const setInclVat = (incl: boolean) => {
    const v = incl ? "incl" : "excl";
    setOverride(v);
    try {
      if (typeof window !== "undefined") localStorage.setItem(STORAGE_KEY, v);
    } catch {}
  };

  const value = useMemo<VatCtx>(
    () => ({
      inclVat,
      userOverride: override !== null,
      setInclVat,
      toggle: () => setInclVat(!inclVat),
    }),
    [inclVat, override],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useVat() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useVat must be used within VatProvider");
  return ctx;
}

/**
 * Reken een prijs (ex btw, in centen) om naar de gekozen weergave.
 * Gebruikt overal dezelfde invariant `incl = ex + round(ex*rate)`, zodat
 * losse regels en totalen nooit een cent uit elkaar lopen.
 */
export function priceForDisplay(cents: number, inclVat: boolean): number {
  return inclVat ? toIncl(cents) : cents;
}

/** Tegenwaarde label, bv. voor secundaire weergave onder hoofdprijs. */
export function counterpartPrice(cents: number, inclVat: boolean): number {
  // cents zijn altijd ex btw; toon de andere kant
  return inclVat ? cents : toIncl(cents);
}

export { vatOf };
